/*
 * Iron Relic motion layer: scroll reveals, hero parallax, press feedback.
 *
 * Shared by all concept themes; each theme tunes it with CSS custom properties
 * on :root (see motion.css). Rules this file follows:
 * - Content is never hidden unless this script runs (html.motion-on gate).
 * - Anything already in the viewport on load is left alone, so the buy box,
 *   prices and headings never flash or shift.
 * - Hero/LCP images are never faded; they only get parallax.
 * - prefers-reduced-motion and the theme editor get no motion at all.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches || window.Shopify?.designMode) return;

  const REVEAL_UNITS = [
    '[data-rv]',
    '.text-block',
    '.button',
    '.button-secondary',
    '.resource-list__item',
    '.collection-card',
    'blockquote',
    '.accordion',
    'details',
  ].join(',');

  const PARALLAX_TARGETS = ['img.hero__media', 'video.hero__media', '.media-with-content img'].join(',');

  const cssNumber = (name, fallback) => {
    const value = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  root.classList.add('motion-on');

  /* ---------- Scroll reveals ---------- */

  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  function prepareReveals(scope) {
    const stagger = cssNumber('--rv-stagger', 60);
    const sections = scope.matches?.('.shopify-section') ? [scope] : scope.querySelectorAll('#MainContent .shopify-section, .shopify-section-group-footer-group');

    sections.forEach((section) => {
      const units = [...section.querySelectorAll(REVEAL_UNITS)].filter(
        // Animate the outermost unit only; nested units ride along with their parent.
        (el) => !el.parentElement.closest(REVEAL_UNITS) && !el.closest('[data-no-reveal], .cart-drawer, dialog, .sticky-add-to-cart')
      );
      let index = 0;
      for (const el of units) {
        if (el.dataset.reveal) continue;
        const rect = el.getBoundingClientRect();
        // Already on screen (or above it) at load: leave it exactly as rendered.
        if (rect.top < window.innerHeight) continue;
        el.dataset.reveal = '';
        el.style.setProperty('--rv-delay', `${Math.min(index++, 6) * stagger}ms`);
        revealObserver.observe(el);
      }
    });
  }

  /* Homepage hero: a short staggered intro for its text and buttons (not its image). */
  function prepareHeroIntro() {
    if (document.getElementById('MainContent')?.dataset.template !== 'index') return;
    const hero = document.querySelector('#MainContent .shopify-section');
    if (!hero) return;
    const stagger = cssNumber('--rv-stagger', 60);
    const intro = '[data-rv], .text-block, .button, .button-secondary';
    const units = [...hero.querySelectorAll(intro)].filter((el) => !el.parentElement.closest(intro));
    units.forEach((el, i) => {
      el.dataset.reveal = 'intro';
      el.style.setProperty('--rv-delay', `${120 + i * stagger * 1.5}ms`);
    });
    // Two frames so the hidden state paints before transitioning in.
    requestAnimationFrame(() => requestAnimationFrame(() => units.forEach((el) => el.classList.add('is-in'))));
  }

  /* ---------- Parallax ---------- */

  const parallaxItems = new Set();
  const visibleItems = new Set();
  let ticking = false;

  const parallaxObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) visibleItems.add(entry.target);
      else visibleItems.delete(entry.target);
    }
    requestTick();
  });

  function prepareParallax(scope) {
    const strength = cssNumber('--px-strength', 0.08);
    if (strength <= 0) return;
    scope.querySelectorAll(PARALLAX_TARGETS).forEach((media) => {
      if (parallaxItems.has(media) || media.getBoundingClientRect().width < 240) return;
      const frame = media.parentElement;
      frame.classList.add('px-frame');
      media.classList.add('px-media');
      parallaxItems.add(media);
      parallaxObserver.observe(frame);
      // Below-the-fold image frames wipe in; the image inside keeps its parallax transform.
      if (!frame.closest('.hero') && frame.getBoundingClientRect().top > window.innerHeight) {
        frame.dataset.reveal = 'clip';
        revealObserver.observe(frame);
      }
    });
  }

  function updateParallax() {
    ticking = false;
    const strength = cssNumber('--px-strength', 0.08);
    const vh = window.innerHeight;
    for (const frame of visibleItems) {
      const media = frame.querySelector('.px-media');
      if (!media) continue;
      const rect = frame.getBoundingClientRect();
      // -1 when the frame's centre is at the bottom of the viewport, +1 at the top.
      const progress = (vh / 2 - (rect.top + rect.height / 2)) / (vh / 2 + rect.height / 2);
      const shift = Math.max(-1, Math.min(1, progress)) * rect.height * strength;
      media.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${1 + strength * 2})`;
    }
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateParallax);
  }

  // Horizon scrolls .page-wrapper (not the window) at 990px and up; scroll events don't
  // bubble, so listen in the capture phase to hear whichever element is scrolling.
  document.addEventListener('scroll', requestTick, { passive: true, capture: true });
  window.addEventListener('resize', requestTick, { passive: true });

  /* ---------- Init + theme editor / section re-render hooks ---------- */

  function init(scope = document) {
    prepareReveals(scope);
    prepareParallax(scope);
    requestTick();
  }

  const start = () => {
    prepareHeroIntro();
    init();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // If the user turns on reduced motion mid-visit, drop everything immediately.
  reduceMotion.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    root.classList.remove('motion-on');
    parallaxItems.forEach((media) => (media.style.transform = ''));
    document.removeEventListener('scroll', requestTick, { capture: true });
  });
})();
